import { useTranslation } from 'next-i18next'
import { HELP_CENTER_TOPICS, HygraphTopics, TOPIC_MAP } from 'src/utils/hygraph'
import HelpOptionCard from './HelpOptionCard'

const topics = HELP_CENTER_TOPICS.map(
  (topic) => TOPIC_MAP[topic as HygraphTopics]
)

interface IProps {
  variant?: 'default' | 'subtle'
  eyebrowKey?: 'CATEGORIES_EYEBROW' | 'CATEGORY_OTHER_EYEBROW'
  titlePrimaryKey?: 'CATEGORIES_TITLE_PRIMARY' | 'CATEGORY_OTHER_TITLE_PRIMARY'
  titleItalicKey?: 'CATEGORIES_TITLE_ITALIC' | 'CATEGORY_OTHER_TITLE_ITALIC'
  showSubtitle?: boolean
  /** The category page's own topic — never list the page you are on under "Otras categorías". */
  excludeTopic?: HygraphTopics
}

const Categories = ({
  variant = 'default',
  eyebrowKey = 'CATEGORIES_EYEBROW',
  titlePrimaryKey = 'CATEGORIES_TITLE_PRIMARY',
  titleItalicKey = 'CATEGORIES_TITLE_ITALIC',
  showSubtitle = true,
  excludeTopic
}: IProps) => {
  const { t } = useTranslation('common')

  // Map enum keys to literal t() calls so TS narrows correctly.
  const eyebrow =
    eyebrowKey === 'CATEGORIES_EYEBROW'
      ? t('HELP.CATEGORIES_EYEBROW')
      : t('HELP.CATEGORY_OTHER_EYEBROW')
  const titlePrimary =
    titlePrimaryKey === 'CATEGORIES_TITLE_PRIMARY'
      ? t('HELP.CATEGORIES_TITLE_PRIMARY')
      : t('HELP.CATEGORY_OTHER_TITLE_PRIMARY')
  const titleItalic =
    titleItalicKey === 'CATEGORIES_TITLE_ITALIC'
      ? t('HELP.CATEGORIES_TITLE_ITALIC')
      : t('HELP.CATEGORY_OTHER_TITLE_ITALIC')

  const wrapperClass =
    variant === 'subtle'
      ? 'border-t border-ink-100/15 bg-white-500 px-5 py-20 md:px-10 md:py-24'
      : 'border-t border-ink-100/15 bg-cream-300 px-5 py-20 md:px-10 md:py-28'

  return (
    <section className={wrapperClass}>
      <div className="mx-auto w-full max-w-7xl">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-12">
          <div className="md:col-span-5">
            <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
              {eyebrow}
            </p>
            <h2 className="mt-3 font-display text-[26px] leading-[1.15] font-medium tracking-[-0.01em] text-ink-700 md:text-[40px]">
              {`${titlePrimary} `}
              <span className="font-display text-blue-500 italic">
                {titleItalic}
              </span>
            </h2>
          </div>
          {showSubtitle && (
            <p className="max-w-md text-[14px] leading-relaxed text-ink-200 md:col-span-7 md:self-end md:text-[15px]">
              {t('HELP.CATEGORIES_SUBTITLE')}
            </p>
          )}
        </div>

        <ul className="mt-10 grid grid-cols-2 gap-px overflow-hidden border-t border-l border-ink-100/15 bg-ink-100/15 md:mt-14 md:grid-cols-3 lg:grid-cols-6">
          {topics
            .filter((topic) => topic.topic !== excludeTopic)
            .map(({ image, label, path, topic }) => (
              <li key={topic} className="bg-white-500">
                <HelpOptionCard image={image ?? ''} title={label} href={path} />
              </li>
            ))}
        </ul>
      </div>
    </section>
  )
}

export default Categories
